import { useEffect, useMemo, useState } from "react";
import type { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useBusy } from "../../context/BusyContext";
import DataTable from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import AdminDataModal from "../components/AdminDataModal";

export default function Brands() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { runBusy } = useBusy();

  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const brandFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },
      { key: "name", label: "Brand Name", colSpan: 2 },
      {
        key: "image",
        label: "Brand Logo",
        type: "image",
        imageType: "single",
        imageFolder: "brands",
        uploadField: "brandImage",
        colSpan: 1,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    ],
    [],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "name", label: "Brand Name" },
    {
      key: "image",
      label: "Logo",
      imageFolder: "brands",
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status
            ? row.status.charAt(0).toUpperCase() + row.status.slice(1)
            : "-"}
        </span>
      ),
    },
  ];

  const fetchBrands = async () => {
    try {
      const res = await runBusy(() =>
        fetch(`${BASE_URL}/admin/brands`, {
          credentials: "include",
        }),
      );

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Brands Error:", error);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  const buildFormData = (formData: any, fields: FormField[]) => {
    const form = new FormData();

    fields.forEach((field) => {
      const value = formData[field.key];

      if (field.type === "image") {
        if (value?.file) {
          form.append(field.uploadField || field.key, value.file);
        } else if (value === "") {
          form.append(field.key, "REMOVE");
        }
      } else {
        form.append(field.key, value ?? "");
      }
    });

    return form;
  };

  const handleSubmit = async (formData: any) => {
    try {
      const form = buildFormData(formData, brandFields);

      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/brands`
          : `${BASE_URL}/admin/brands/${activeRow.id}`;

      const res = await runBusy(() =>
        fetch(url, {
          method,
          credentials: "include",
          body: form,
        }),
      );

      const result = await res.json();

      if (!res.ok || result.success === false) {
        showModal({
          type: "error",
          title: "Error",
          message:
            result.message ||
            (modalMode === "add"
              ? "Failed to add brand."
              : "Failed to update brand."),
          confirmText: "OK",
        });
        return;
      }

      await fetchBrands();

      setModalOpen(false);
      setActiveRow(null);

      showModal({
        type: "success",
        title: "Success",
        message:
          result.message ||
          (modalMode === "add"
            ? "Brand added successfully."
            : "Brand updated successfully."),
        confirmText: "OK",
      });
    } catch (error: any) {
      console.error("Brand Submit Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message:
          error?.message ||
          (modalMode === "add"
            ? "Failed to add brand."
            : "Failed to update brand."),
        confirmText: "OK",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Brand",
      message: `Are you sure you want to delete "${row.name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await runBusy(() =>
            fetch(`${BASE_URL}/admin/brands/${row.id}`, {
              method: "DELETE",
              credentials: "include",
            }),
          );

          const result = await res.json();

          if (!res.ok || result.success === false) {
            showModal({
              type: "error",
              title: "Error",
              message: result.message || "Failed to delete brand.",
              confirmText: "OK",
            });
            return;
          }

          await fetchBrands();
          setSelectedRow(null);

          showModal({
            type: "success",
            title: "Deleted",
            message: result.message || "Brand deleted successfully.",
            confirmText: "OK",
          });
        } catch (error) {
          console.error("Delete Brand Error:", error);

          showModal({
            type: "error",
            title: "Error",
            message: "Failed to delete brand.",
            confirmText: "OK",
          });
        }
      },
    });
  };

  return (
    <div>
      <AdminListToolkit
        title="Brands"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={true}
        exportFileName="brands"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Brand"
        fields={brandFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}